import { Link } from "react-router-dom";
import { Facebook, Twitter, Instagram, Linkedin, Youtube, Mail, Phone, MapPin } from "lucide-react";
import { useSiteConfig } from "../../context/SiteConfigContext";
const SocialIcon = ({ Icon, url, label }) => {
    if (!url) return null;
    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={label}
            className="h-9 w-9 rounded-sm border border-white/20 inline-flex items-center justify-center hover:bg-white/10 transition-colors"
            data-testid={`social-${label.toLowerCase()}`}
        >
            <Icon size={16} />
        </a>
    );
};

export const Footer = () => {
    const { config } = useSiteConfig();
    if (!config) return null;
    return (
        <footer
            className="mt-24"
            style={{ background: "hsl(var(--footer-bg))", color: "hsl(var(--footer-text))" }}
            data-testid="site-footer"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
                <div className="md:col-span-5">
                    <div className="font-display text-3xl font-semibold tracking-tight mb-4">
                        {config.site_name}
                    </div>
                    <p className="text-sm/relaxed opacity-80 max-w-md">{config.footer_text}</p>
                    <div className="mt-6 flex gap-2">
                        <SocialIcon Icon={Facebook} url={config.social_facebook} label="Facebook" />
                        <SocialIcon Icon={Twitter} url={config.social_twitter} label="Twitter" />
                        <SocialIcon Icon={Instagram} url={config.social_instagram} label="Instagram" />
                        <SocialIcon Icon={Linkedin} url={config.social_linkedin} label="LinkedIn" />
                        <SocialIcon Icon={Youtube} url={config.social_youtube} label="YouTube" />
                    </div>
                </div>

                <div className="md:col-span-3">
                    <h4 className="text-xs uppercase tracking-[0.18em] opacity-70 mb-4">Explore</h4>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/about" className="hover:underline">About YACRRD</Link></li>
                        <li><Link to="/programs" className="hover:underline">Our Programs</Link></li>
                        <li><Link to="/gallery" className="hover:underline">Photo Gallery</Link></li>
                        <li><Link to="/news" className="hover:underline">News & Stories</Link></li>
                        <li><Link to="/resources" className="hover:underline">Resources</Link></li>
                        <li><Link to="/partners" className="hover:underline">Partners & Donors</Link></li>
                        <li><Link to="/careers" className="hover:underline">Careers / Jobs</Link></li>
                        <li><Link to="/donate" className="hover:underline">Donate</Link></li>
                        <li><Link to="/contact" className="hover:underline">Contact / Partner</Link></li>
                    </ul>
                </div>

                <div className="md:col-span-4">
                    <h4 className="text-xs uppercase tracking-[0.18em] opacity-70 mb-4">Get in Touch</h4>
                    <ul className="space-y-3 text-sm">
                        {config.contact_email && (
                            <li className="flex items-start gap-3">
                                <Mail size={16} className="mt-0.5 opacity-70 shrink-0" />
                                <a href={`mailto:${config.contact_email}`} className="hover:underline" data-testid="footer-email">{config.contact_email}</a>
                            </li>
                        )}
                        {config.contact_phone && (
                            <li className="flex items-start gap-3">
                                <Phone size={16} className="mt-0.5 opacity-70 shrink-0" />
                                <a href={`tel:${config.contact_phone}`} className="hover:underline" data-testid="footer-phone">{config.contact_phone}</a>
                            </li>
                        )}
                        {config.contact_address && (
                            <li className="flex items-start gap-3">
                                <MapPin size={16} className="mt-0.5 opacity-70 shrink-0" />
                                <span>{config.contact_address}</span>
                            </li>
                        )}
                    </ul>
                </div>
            </div>
            <div className="border-t border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs opacity-70">
                    <div>© {new Date().getFullYear()} {config.site_name}. All rights reserved.</div>
                    <div className="opacity-80">{config.tagline}</div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
